import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-list-article',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        ToastModule,
        ConfirmDialogModule,
        TagModule,
        PaginatorModule
    ],
    providers: [MessageService, ConfirmationService],
    styles: `
        .title-icon {
            width: 4rem;
            height: 4rem;
            background: linear-gradient(90deg, #2162DF 100%, #558BED 0%);
            border-radius: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }
    `,
    template: `
        <div class="max-w-7xl mx-auto px-6 py-8">
            <!-- Header with Add Button -->
            <div class="flex justify-between items-center mb-4">
                <div class="flex items-center">
                    <div class="title-icon mr-3">
                        <i class="pi pi-file" style="font-size: 2rem"></i>
                    </div>
                    <div>
                        <h1 class="text-3xl mb-0 font-bold text-gray-900">Artikel Medis</h1>
                        <p class="text-gray-600">Daftar konten artikel untuk platform</p>
                    </div>
                </div>
            </div>

            <div class="rounded-xl shadow-sm mb-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div class="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.3-4.3"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Cari artikel..."
                            [(ngModel)]="searchQuery"
                            (input)="filterArticles()"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    </div>
                </div>
                
                <!-- Category Badges -->
                <div class="flex flex-wrap gap-2">
                    <button
                        (click)="selectCategory('')"
                        [class]="selectedCategory === '' ? 'px-4 py-2 rounded-full text-sm font-semibold transition bg-blue-600 text-white shadow-md' : 'px-4 py-2 rounded-full text-sm font-semibold transition bg-gray-100 text-gray-700 hover:bg-gray-200'">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block mr-1">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        Semua
                    </button>
                    <button
                        *ngFor="let category of categories"
                        (click)="selectCategory(category)"
                        [class]="selectedCategory === category ? 'px-4 py-2 rounded-full text-sm font-semibold transition bg-blue-600 text-white shadow-md' : 'px-4 py-2 rounded-full text-sm font-semibold transition bg-gray-100 text-gray-700 hover:bg-gray-200'">
                        {{ category }}
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                    *ngFor="let article of paginatedArticles"
                    class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition cursor-pointer group relative"
                    (click)="viewArticle(article)">
                    <div class="relative aspect-video">
                        <img *ngIf="article.gambar" [src]="getImageUrl(article.gambar)" [alt]="article.judul" class="w-full h-full object-cover" loading="lazy">
                        <div *ngIf="!article.gambar" class="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                            <i class="pi pi-file" style="font-size: 4.5rem;color:white;"></i>
                        </div>
                        <div class="absolute top-3 left-3 bg-white text-black px-3 py-1 rounded text-sm font-semibold">
                            {{ article.kategori }}
                        </div>
                        <div *ngIf="article.read_time" class="absolute bottom-3 right-3 bg-black bg-opacity-60 text-white px-3 py-1 rounded text-xs font-semibold">
                            <i class="pi pi-clock text-xs mr-1"></i> {{ article.read_time }}
                        </div>
                    </div>
                    <div class="p-4">
                        <h3 class="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {{ article.judul }}
                        </h3>
                        <p class="text-sm text-gray-600 mb-3 line-clamp-2">
                            {{ article.excerpt || 'Tidak ada excerpt tersedia.' }}
                        </p>
                        <div class="flex items-center text-xs text-gray-500 gap-3">
                            <span *ngIf="article.author"><i class="pi pi-user mr-1"></i> {{ article.author }}</span>
                            <span *ngIf="article.tanggal"><i class="pi pi-calendar mr-1"></i> {{ formatDate(article.tanggal) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div *ngIf="totalRecords > rows" class="flex justify-center mt-8">
                <p-paginator
                    [first]="first"
                    [rows]="rows"
                    [totalRecords]="totalRecords"
                    [rowsPerPageOptions]="[6, 9, 12, 18]"
                    (onPageChange)="onPageChange($event)">
                </p-paginator>
            </div>

            <!-- Empty State -->
            <div *ngIf="filteredArticles.length === 0 && !loading" class="text-center py-12">
                <i class="pi pi-file text-4xl text-gray-400 mb-4 block"></i>
                <p class="text-gray-500">Tidak ada artikel yang ditemukan</p>
            </div>

            <!-- Loading State -->
            <div *ngIf="loading" class="text-center py-12">
                <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
                <p class="mt-2 text-gray-600">Memuat artikel...</p>
            </div>
        </div>

        <p-confirmDialog></p-confirmDialog>
        <p-toast></p-toast>
    `
})
export class ListArticle implements OnInit {
    articles: any[] = [];
    filteredArticles: any[] = [];
    paginatedArticles: any[] = [];
    loading = false;
    searchQuery = '';
    selectedCategory = '';
    isAuthorizedUser = false;
    categories: string[] = [];

    // Pagination properties
    first = 0;
    rows = 9;
    totalRecords = 0;

    constructor(
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private router: Router
    ) {}

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadArticles();
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            
            if (userData) {
                const user = JSON.parse(userData);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY';
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.isAuthorizedUser = false;
        }
    }

    loadArticles() {
        this.loading = true;
        this.userService.getArticles().then(
            (response) => {
                const data = response?.data || response || [];
                this.articles = Array.isArray(data) ? data : [];
                this.extractCategories();
                this.filterArticles();
                this.loading = false;
            },
            (error) => {
                if (error && (error.status === 404 || error.status === 204)) {
                    this.articles = [];
                    this.filterArticles();
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Gagal memuat data artikel'
                    });
                }
                this.loading = false;
            }
        );
    }

    filterArticles() {
        this.filteredArticles = this.articles.filter(article => {
            const matchesSearch = !this.searchQuery ||
                article.judul.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                (article.excerpt && article.excerpt.toLowerCase().includes(this.searchQuery.toLowerCase()));

            const matchesCategory = !this.selectedCategory || article.kategori === this.selectedCategory;

            return matchesSearch && matchesCategory;
        });

        this.first = 0;
        this.totalRecords = this.filteredArticles.length;
        this.updatePaginatedArticles();
    }

    selectCategory(category: string) {
        this.selectedCategory = category;
        this.filterArticles();
    }

    extractCategories() {
        const categorySet = new Set<string>();
        this.articles.forEach(article => {
            if (article.kategori) {
                categorySet.add(article.kategori);
            }
        });
        this.categories = Array.from(categorySet).sort();
    }

    updatePaginatedArticles() {
        const start = this.first;
        const end = this.first + this.rows;
        this.paginatedArticles = this.filteredArticles.slice(start, end);
    }

    onPageChange(event: any) {
        this.first = event.first;
        this.rows = event.rows;
        this.updatePaginatedArticles();
    }

    createNew() {
        this.router.navigate(['/app/article/create']);
    }

    viewArticle(article: any) {
        this.router.navigate(['/app/article/show', article.id]);
    }

    editArticle(event: Event, article: any) {
        event.stopPropagation();
        this.router.navigate(['/app/article/edit', article.id]);
    }

    deleteArticle(event: Event, article: any) {
        event.stopPropagation();
        this.confirmationService.confirm({
            message: `Apakah Anda yakin ingin menghapus artikel "${article.judul}"?`,
            header: 'Konfirmasi Hapus',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ya, Hapus',
            rejectLabel: 'Batal',
            accept: () => {
                this.userService.deleteArticle({ id: article.id }).then(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Artikel berhasil dihapus'
                        });
                        // Hapus langsung dari list lokal agar UI responsif seketika
                        this.articles = this.articles.filter(a => a.id !== article.id);
                        this.filterArticles();
                        this.loadArticles();
                    },
                    (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Gagal menghapus artikel'
                        });
                    }
                );
            }
        });
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    getImageUrl(path: string | null | undefined): string {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
            return `http://localhost:8000${path.startsWith('/') ? '' : '/'}${path}`;
        }
        return `https://oscemediko.id${path.startsWith('/') ? '' : '/'}${path}`;
    }
}
